// MyInput.tsx - Fixed with complete dark mode support
import React, { useMemo, useEffect, useRef, useCallback } from "react";
import Select, { type SingleValue, type MultiValue } from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Range, getTrackBackground } from "react-range";
import { SwitchField } from "../MY_FORM/fields/SwitchField";
import CreatableSelect from "react-select/creatable";
import { ImageUploader } from "../../components/ImageUploader";
import { FileUploaderWithProgress } from "../../components/FileUploaderWithProgress";
import LocationSelector from "../My_MapTools/LocationSelector";
import MyWordStyleEditor from "../HtmlJson/MyWordStyleTipTapEditor";
import DhivehiTextField from "./DhivehiInput/DhivehiTextField";
import ArabicTextField from "./ArabicInput/ArabicTextField";

export interface CustomOption {
  value: string | number | boolean;
  label: string;
  icon?: string;
}

export interface Field {
  label: string;
  name: string;
  type?:
    | "text"
    | "dhivehitext"
    | "arabictext"
    | "tiptap"
    | "number"
    | "email"
    | "password"
    | "select"
    | "multiselect"
    | "creatable-select"
    | "boolean"
    | "date"
    | "datetime"
    | "range"
    | "file"
    | "maplocation"
    | "image";
  required?: boolean;
  options?: CustomOption[] | string[];
  placeholder?: string;
  hidden?: boolean;
  disabled?: boolean;
  actionBtn?: React.ReactNode;
  multiple?: boolean;
  min?: number;
  max?: number;
  step?: number;
  showIf?: any;
  dependsOn?: string;
  getOptions?: (parentValue: any) => CustomOption[] | string[];
  value?: any;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  helperText?: string;
}

interface MyInputProps {
  field: Field;
  formData?: Record<string, any>;
  setFormData?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

const classNames = (...classes: string[]) => classes.filter(Boolean).join(" ");

// Custom styles for react-select with dark mode support
const getSelectStyles = (error?: string, isDark?: boolean) => ({
  menuPortal: (base: any) => ({ ...base, zIndex: 999999 }),
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    borderColor: error 
      ? "#ef4444" 
      : state.isFocused 
        ? "#3b82f6" 
        : (isDark ? '#4b5563' : '#d1d5db'),
    boxShadow: error 
      ? "0 0 0 1px #ef4444" 
      : state.isFocused 
        ? "0 0 0 1px #3b82f6" 
        : "none",
    "&:hover": {
      borderColor: error ? "#ef4444" : "#3b82f6",
    },
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? (isDark ? '#374151' : '#f3f4f6')
        : 'transparent',
    color: state.isSelected 
      ? "#ffffff" 
      : (isDark ? '#f3f4f6' : '#1f2937'),
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#3b82f6",
    },
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? '#f3f4f6' : '#1f2937',
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: isDark ? '#374151' : '#e5e7eb',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: isDark ? '#f3f4f6' : '#1f2937',
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: isDark ? '#f3f4f6' : '#6b7280',
    ":hover": {
      backgroundColor: "#ef4444",
      color: "#ffffff",
    },
  }),
  input: (base: any) => ({
    ...base,
    color: isDark ? '#f3f4f6' : '#1f2937',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? '#6b7280' : '#9ca3af',
  }),
  clearIndicator: (base: any) => ({
    ...base,
    color: isDark ? '#6b7280' : '#9ca3af',
    ":hover": {
      color: "#ef4444",
    },
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: isDark ? '#6b7280' : '#9ca3af',
    ":hover": {
      color: "#3b82f6",
    },
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: isDark ? '#4b5563' : '#e5e7eb',
  }),
});

// Custom styles for DatePicker with dark mode
const getDatePickerStyles = () => {
  // Add dark mode styles to document head if not already there
  if (typeof document !== 'undefined' && !document.querySelector('#datepicker-dark-styles')) {
    const style = document.createElement('style');
    style.id = 'datepicker-dark-styles';
    style.textContent = `
      .react-datepicker {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
        color: #f3f4f6 !important;
      }
      .react-datepicker__header {
        background-color: #374151 !important;
        border-bottom-color: #4b5563 !important;
      }
      .react-datepicker__current-month,
      .react-datepicker__day-name,
      .react-datepicker__day {
        color: #f3f4f6 !important;
      }
      .react-datepicker__day:hover {
        background-color: #4b5563 !important;
      }
      .react-datepicker__day--selected {
        background-color: #3b82f6 !important;
        color: white !important;
      }
      .react-datepicker__day--keyboard-selected {
        background-color: #3b82f6 !important;
      }
      .react-datepicker__navigation-icon::before {
        border-color: #9ca3af !important;
      }
      .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: #3b82f6 !important;
      }
      .react-datepicker__time-container {
        background-color: #1f2937 !important;
        border-left-color: #374151 !important;
      }
      .react-datepicker__time-container .react-datepicker__time {
        background-color: #1f2937 !important;
      }
      .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
        color: #f3f4f6 !important;
      }
      .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
        background-color: #374151 !important;
      }
      .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
        background-color: #3b82f6 !important;
      }
      .react-datepicker__month-select,
      .react-datepicker__year-select {
        background-color: #374151 !important;
        color: #f3f4f6 !important;
        border-color: #4b5563 !important;
      }
    `;
    document.head.appendChild(style);
  }
};

const MyInput: React.FC<MyInputProps> = ({ field, formData = {}, setFormData }) => {
  // Detect dark mode
  const isDarkMode = typeof window !== 'undefined' 
    ? document.documentElement.classList.contains('dark') || 
      (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
    : false;

  // Apply datepicker styles on mount
  useEffect(() => {
    getDatePickerStyles();
  }, []);

  // Support both direct value/onChange (react-hook-form) and formData/setFormData (legacy)
  const isControlled = field.onChange !== undefined;
  
  const value = isControlled 
    ? field.value 
    : (formData[field.name] ?? (field.type === "range" ? [field.min ?? 0, field.max ?? 100] : ""));
  
  const error = field.error;
  const helperText = field.helperText;

  const handleChange = useCallback((name: string, val: any) => {
    if (isControlled && field.onChange) {
      field.onChange(val);
    } else if (setFormData) {
      setFormData(prev => ({ ...prev, [name]: val }));
    }
  }, [isControlled, field, setFormData]);

  const handleBlur = useCallback(() => {
    if (field.onBlur) {
      field.onBlur();
    }
  }, [field]);

  // Helper to render label with required indicator
  const renderLabel = () => {
    if (!field.label) return null;
    return (
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
  };

  // Helper to render error/helper text
  const renderHelperText = () => {
    if (error) {
      return <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>;
    }
    if (helperText) {
      return <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{helperText}</p>;
    }
    return null;
  };

  // Helper to get input classes with error styling
  const getInputClasses = (baseClasses: string = "") => {
    return classNames(
      baseClasses,
      error 
        ? "border-red-500 dark:border-red-400 focus:ring-red-500" 
        : "border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500",
      field.disabled 
        ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60" 
        : "bg-white dark:bg-gray-900",
      "focus:outline-none focus:ring-2 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
    );
  };

  if (field.hidden) {
    return null;
  }

  // ---------- RANGE ----------
  if (field.type === "range") {
    const STEP = field.step || 1;
    const MIN = field.min ?? 0;
    const MAX = field.max ?? 100;
    const rangeValue = Array.isArray(value) ? value : [MIN, MAX];

    return (
      <div className="mb-4">
        {renderLabel()}
        <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          {rangeValue[0]} - {rangeValue[1]}
        </div>
        <Range
          values={rangeValue}
          step={STEP}
          min={MIN}
          max={MAX}
          onChange={(vals) => handleChange(field.name, vals)}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className="h-2 w-full rounded bg-gray-200 dark:bg-gray-700 relative"
              style={{
                background: getTrackBackground({
                  values: rangeValue,
                  colors: ["#3b82f6", isDarkMode ? "#4b5563" : "#d1d5db", "#3b82f6"],
                  min: MIN,
                  max: MAX,
                }),
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              className="h-5 w-5 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-full shadow-md flex items-center justify-center cursor-pointer"
            />
          )}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- SELECT / MULTISELECT / CREATABLE ----------
  if (
    field.type === "select" ||
    field.type === "multiselect" ||
    field.type === "creatable-select"
  ) {
    const isMulti = field.type === "multiselect";
    const isCreatable = field.type === "creatable-select";

    const Component = isCreatable ? CreatableSelect : Select;

    // Get options (support both direct options and getOptions function)
    const selectOptions = useMemo(() => {
      let rawOptions: CustomOption[] | string[] = [];

      if (field.dependsOn && field.getOptions && formData) {
        const parentValue = formData[field.dependsOn];
        rawOptions = field.getOptions(parentValue) || [];
      } else if (field.options) {
        rawOptions = field.options;
      }

      return rawOptions.map(opt =>
        typeof opt === "string" ? { label: opt, value: opt } : opt
      );
    }, [field.options, field.getOptions, field.dependsOn, formData]);

    const selectedValue = isMulti
      ? selectOptions.filter(opt => Array.isArray(value) && value.includes(opt.value))
      : selectOptions.find(opt => opt.value === value) || null;

    const handleSelectChange = (
      opt: SingleValue<CustomOption> | MultiValue<CustomOption>
    ) => {
      if (isMulti) {
        handleChange(field.name, (opt as MultiValue<CustomOption>).map(o => o.value));
      } else {
        handleChange(field.name, (opt as SingleValue<CustomOption>)?.value ?? "");
      }
    };

    return (
      <div className="mb-4">
        <div className="flex justify-between items-center gap-2">
          <div className="flex-1">
            {renderLabel()}
            <Component
              isMulti={isMulti}
              options={selectOptions}
              value={selectedValue as any}
              onChange={handleSelectChange}
              onBlur={handleBlur}
              isDisabled={field.disabled}
              placeholder={field.placeholder || "Select or create"}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition="fixed"
              styles={getSelectStyles(error, isDarkMode)}
              theme={(theme) => ({
                ...theme,
                borderRadius: 8,
                colors: {
                  ...theme.colors,
                  primary: "#3b82f6",
                  primary75: "#60a5fa",
                  primary50: "#93c5fd",
                  primary25: isDarkMode ? "#374151" : "#bfdbfe",
                  danger: "#ef4444",
                  dangerLight: "#fecaca",
                  neutral0: isDarkMode ? "#1f2937" : "#ffffff",
                  neutral5: isDarkMode ? "#374151" : "#f9fafb",
                  neutral10: isDarkMode ? "#4b5563" : "#f3f4f6",
                  neutral20: isDarkMode ? "#6b7280" : "#e5e7eb",
                  neutral30: isDarkMode ? "#9ca3af" : "#d1d5db",
                  neutral40: isDarkMode ? "#d1d5db" : "#9ca3af",
                  neutral50: isDarkMode ? "#9ca3af" : "#6b7280",
                  neutral60: isDarkMode ? "#d1d5db" : "#4b5563",
                  neutral70: isDarkMode ? "#e5e7eb" : "#374151",
                  neutral80: isDarkMode ? "#f3f4f6" : "#1f2937",
                  neutral90: isDarkMode ? "#f9fafb" : "#111827",
                },
              })}
            />
          </div>
          {field.actionBtn && <div className="flex-shrink-0">{field.actionBtn}</div>}
        </div>
        {renderHelperText()}
      </div>
    );
  }

  // ---------- MAP LOCATION ----------
  if (field.type === "maplocation") {
    const parsedValue: [number, number] | null =
      typeof value === "string" && value.includes(",")
        ? (value.split(",").map(Number) as [number, number])
        : null;

    return (
      <div className="flex flex-col gap-2 mb-4">
        {renderLabel()}
        <LocationSelector
          value={parsedValue}
          onChange={(lat, lng) => {
            const locationString = `${lat},${lng}`;
            handleChange(field.name, locationString);
          }}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- TIPTAP EDITOR ----------
  if (field.type === "tiptap") {
    return (
      <div className="mb-4">
        {renderLabel()}
        <MyWordStyleEditor
          value={value || null}
          onSave={(val) => handleChange(field.name, val)}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- DHIVEHI TEXT ----------
  if (field.type === "dhivehitext") {
    return (
      <div className="mb-4">
        {renderLabel()}
        <DhivehiTextField
          value={value || ""}
          onChange={(val) => handleChange(field.name, val)}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- ARABIC TEXT ----------
  if (field.type === "arabictext") {
    return (
      <div className="mb-4">
        {renderLabel()}
        <ArabicTextField
          value={value || ""}
          onChange={(val) => handleChange(field.name, val)}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- BOOLEAN / SWITCH ----------
  if (field.type === "boolean") {
    return (
      <div className="mb-4">
        <SwitchField
          field={{ ...field, value: !!value }}
          value={!!value}
          onChange={(val) => handleChange(field.name, val)}
          disabled={field.disabled}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- DATE PICKER ----------
  if (field.type === "date") {
    const dateValue = value ? new Date(value) : null;
    return (
      <div className="mb-4">
        {renderLabel()}
        <DatePicker
          selected={dateValue}
          onChange={(date) => handleChange(field.name, date)}
          onBlur={handleBlur}
          dateFormat="yyyy-MM-dd"
          className={getInputClasses("w-full rounded-lg px-3 py-2 border")}
          placeholderText={field.placeholder || "Select a date"}
          disabled={field.disabled}
          popperClassName={isDarkMode ? "dark-theme" : ""}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- DATETIME LOCAL ----------
  if (field.type === "datetime") {
    const datetimeValue = value ? (typeof value === 'string' ? value.slice(0, 16) : "") : "";
    return (
      <div className="mb-4">
        {renderLabel()}
        <input
          type="datetime-local"
          value={datetimeValue}
          onChange={(e) => handleChange(field.name, e.target.value)}
          onBlur={handleBlur}
          disabled={field.disabled}
          className={getInputClasses("w-full rounded-lg px-3 py-2 border")}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- IMAGE UPLOAD ----------
  if (field.type === "image") {
    const imageUrl = value as string | null;
    return (
      <div className="mb-4">
        {renderLabel()}
        <ImageUploader
          resize
          onUploadComplete={(url) => handleChange(field.name, url)}
        />
        {imageUrl && (
          <div className="mt-3">
            <img
              src={imageUrl}
              alt="Uploaded"
              className="h-24 w-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
            />
          </div>
        )}
        {renderHelperText()}
      </div>
    );
  }

  // ---------- FILE UPLOAD ----------
  if (field.type === "file") {
    const urls: string[] = Array.isArray(value) ? value : value ? [value] : [];

    const handleUpload = (url: string) => {
      if (field.multiple) {
        handleChange(field.name, [...urls, url]);
      } else {
        handleChange(field.name, url);
      }
    };

    const handleRemove = (index: number) => {
      if (field.multiple) {
        handleChange(field.name, urls.filter((_, i) => i !== index));
      } else {
        handleChange(field.name, null);
      }
    };

    return (
      <div className="mb-4">
        {renderLabel()}
        <FileUploaderWithProgress
          accept={field.accept || "application/pdf"}
          folder={field.folder || "uploads/files"}
          onUploadComplete={handleUpload}
        />
        {urls.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
            {urls.map((url, i) => (
              <li key={i} className="flex items-center justify-between border border-gray-300 dark:border-gray-600 p-2 rounded-lg">
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="truncate max-w-[220px] text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {url.split("/").pop()}
                </a>
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="text-red-500 dark:text-red-400 text-xs hover:underline"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {renderHelperText()}
      </div>
    );
  }

  // ---------- NUMBER INPUT ----------
  if (field.type === "number") {
    return (
      <div className="mb-4">
        {renderLabel()}
        <input
          type="number"
          name={field.name}
          value={value}
          onChange={(e) => handleChange(field.name, e.target.valueAsNumber)}
          onBlur={handleBlur}
          required={field.required}
          placeholder={field.placeholder}
          disabled={field.disabled}
          min={field.min}
          max={field.max}
          step={field.step}
          className={getInputClasses("w-full rounded-lg px-3 py-2 border")}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- TEXTAREA ----------
  if (field.type === "textarea") {
    return (
      <div className="mb-4">
        {renderLabel()}
        <textarea
          name={field.name}
          value={value}
          onChange={(e) => handleChange(field.name, e.target.value)}
          onBlur={handleBlur}
          required={field.required}
          placeholder={field.placeholder}
          disabled={field.disabled}
          rows={4}
          className={getInputClasses("w-full rounded-lg px-3 py-2 border resize-vertical")}
        />
        {renderHelperText()}
      </div>
    );
  }

  // ---------- DEFAULT TEXT INPUT ----------
  return (
    <div className="mb-4">
      {renderLabel()}
      <input
        type={field.type || "text"}
        name={field.name}
        value={value}
        onChange={(e) => handleChange(field.name, e.target.value)}
        onBlur={handleBlur}
        required={field.required}
        placeholder={field.placeholder}
        disabled={field.disabled}
        className={getInputClasses("w-full rounded-lg px-3 py-2 border")}
      />
      {renderHelperText()}
    </div>
  );
};

export default MyInput;